import React, { useState, useEffect } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    Row,
    Col,
    Label,
    Input,
    Button,
    FormFeedback
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { createOperator, updateOperator, getOperators } from "../../slices/operators/thunk";
import * as Yup from "yup";
import { useFormik } from "formik";

const CreateOperatorModal = ({ isOpen, toggle, operatorToEdit }) => {
    const dispatch = useDispatch();
    const { actionLoading } = useSelector((state) => state.Operators);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Validation Schema
    const validationSchema = Yup.object({
        username: Yup.string().required("Please Enter Username"),
        // Password only required for new operators
        password: !operatorToEdit
            ? Yup.string()
                .required("Please Enter Password")
                .min(6, "Password must be at least 6 characters")
            : Yup.string(),
        confirmPassword: !operatorToEdit
            ? Yup.string()
                .required("Please Enter Confirm Password")
                .oneOf([Yup.ref('password'), null], 'Passwords must match')
            : Yup.string(),
        isActive: Yup.boolean()
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            username: operatorToEdit ? operatorToEdit.username : "",
            password: "",
            confirmPassword: "",
            isActive: operatorToEdit ? operatorToEdit.isActive : true
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            if (operatorToEdit) {
                // Update
                const updateData = {
                    username: values.username,
                    isActive: values.isActive
                };
                const result = await dispatch(updateOperator({ id: operatorToEdit._id, data: updateData }));
                if (result.payload && (result.payload.success || result.payload.status === 1)) {
                    dispatch(getOperators({ page: 1, limit: 10 })); // Refresh list
                    toggle();
                    formik.resetForm();
                }
            } else {
                // Create
                const createData = {
                    username: values.username,
                    password: values.password
                };
                const result = await dispatch(createOperator(createData));
                if (result.payload && (result.payload.success || result.payload.status === 1)) {
                    dispatch(getOperators({ page: 1, limit: 10 })); // Refresh list
                    toggle();
                    formik.resetForm();
                }
            }
        },
    });

    // Reset form on open/close or edit mode change
    useEffect(() => {
        if (!isOpen) {
            formik.resetForm();
            setShowPassword(false);
            setShowConfirmPassword(false);
        }
    }, [isOpen]);

    const generatePassword = () => {
        let newPassword = "";
        for (let i = 0; i < 6; i++) {
            newPassword += Math.floor(Math.random() * 10);
        }
        formik.setFieldValue("password", newPassword);
        formik.setFieldValue("confirmPassword", newPassword);
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>
                {operatorToEdit ? "Edit Operator" : "Create New Operator"}
            </ModalHeader>
            <ModalBody>
                <Form onSubmit={(e) => {
                    e.preventDefault();
                    formik.handleSubmit();
                    return false;
                }}>
                    <Row>
                        <Col md={12}>
                            <div className="mb-3">
                                <Label className="form-label">Username</Label>
                                <Input
                                    type="text"
                                    name="username"
                                    placeholder="Enter username"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.username}
                                    invalid={formik.touched.username && formik.errors.username ? true : false}
                                />
                                {formik.touched.username && formik.errors.username ? (
                                    <FormFeedback>{formik.errors.username}</FormFeedback>
                                ) : null}
                            </div>
                        </Col>


                        {!operatorToEdit && (
                            <>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <Label className="form-label">Password</Label>
                                        <div className="position-relative auth-pass-inputgroup mb-3">
                                            <Input
                                                type={showPassword ? "text" : "password"}
                                                name="password"
                                                placeholder="Enter password"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.password}
                                                invalid={formik.touched.password && formik.errors.password ? true : false}
                                            />
                                            <button
                                                className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                <i className={`ri-${showPassword ? "eye" : "eye-off"}-fill align-middle`}></i>
                                            </button>
                                            {formik.touched.password && formik.errors.password ? (
                                                <FormFeedback>{formik.errors.password}</FormFeedback>
                                            ) : null}
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <Label className="form-label">Confirm Password</Label>
                                        <div className="position-relative auth-pass-inputgroup mb-3">
                                            <Input
                                                type={showConfirmPassword ? "text" : "password"}
                                                name="confirmPassword"
                                                placeholder="Confirm password"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.confirmPassword}
                                                invalid={formik.touched.confirmPassword && formik.errors.confirmPassword ? true : false}
                                            />
                                            <button
                                                className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            >
                                                <i className={`ri-${showConfirmPassword ? "eye" : "eye-off"}-fill align-middle`}></i>
                                            </button>
                                            {formik.touched.confirmPassword && formik.errors.confirmPassword ? (
                                                <FormFeedback>{formik.errors.confirmPassword}</FormFeedback>
                                            ) : null}
                                        </div>
                                    </div>
                                </Col>
                                <Col md={12}>
                                    <Button
                                        color="info"
                                        type="button"
                                        size="sm"
                                        className="mb-3"
                                        onClick={generatePassword}
                                    >
                                        <i className="ri-key-2-line me-1"></i> Generate Strong Password
                                    </Button>
                                </Col>
                            </>
                        )}

                        {operatorToEdit && (
                            <Col md={12}>
                                <div className="form-check form-switch mb-3">
                                    <Input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="isActiveSwitch"
                                        name="isActive"
                                        checked={formik.values.isActive}
                                        onChange={formik.handleChange}
                                    />
                                    <Label className="form-check-label" htmlFor="isActiveSwitch">
                                        Is Active
                                    </Label>
                                </div>
                            </Col>
                        )}
                    </Row>
                    <div className="hstack gap-2 justify-content-end">
                        <Button color="light" onClick={toggle}>Close</Button>
                        <Button color="primary" type="submit" disabled={actionLoading}>
                            {actionLoading ? "Saving..." : operatorToEdit ? "Update Operator" : "Create Operator"}
                        </Button>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
};

export default CreateOperatorModal;
